import axios from "axios";
import cache from "../utils/cache";
import { getAccessToken, getRefreshToken, saveAccessToken, clearTokens, saveRefreshToken } from "../utils/token";

// Global toast function - will be set by ToastProvider
let globalShowError = null;

export const setGlobalToastError = (showErrorFn) => {
  globalShowError = showErrorFn;
};

const sanitizeBaseUrl = (url) => {
  // In development, always use empty string (relative URLs) so proxy handles requests
  // The setupProxy.js will proxy /api requests to the backend
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative URLs - proxy will handle forwarding
  }
  
  // Production: use full URL
  if (!url) {
    return '';
  }
  
  // Remove trailing slash if present
  let cleanUrl = url.endsWith("/") ? url.slice(0, -1) : url;
  
  // If env variable includes /api/v1, strip it since endpoints already include /api/v1
  if (cleanUrl.endsWith("/api/v1")) {
    cleanUrl = cleanUrl.slice(0, -7); // Remove "/api/v1"
  }
  
  return cleanUrl;
};

// Base URL from environment variable
// In development: use empty string (relative URLs) so setupProxy.js handles proxying
// In production: use full URL
const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
const baseURL = sanitizeBaseUrl(envBaseUrl);


const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
  withCredentials: false, // Set to false to avoid CORS issues with credentials
});

// Request interceptor - Add auth token and check cache
apiClient.interceptors.request.use(
  (config) => {
    // Add Authorization header if token exists
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If data is FormData, remove Content-Type header to let axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Only cache GET requests
    if (config.method === "get" && !config.skipCache) {
      const cacheKey = cache.generateKey(config.url, config.params);
      const cachedData = cache.get(cacheKey);
      
      if (cachedData) {
        // Return cached data as a resolved promise
        return Promise.reject({
          __cached: true,
          data: cachedData,
        });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor - Cache successful GET responses and handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    const config = response.config;
    
    // For blob responses (e.g., PDF downloads), return the full response
    // to preserve the blob data integrity
    if (config?.responseType === 'blob' || response.data instanceof Blob) {
      return response;
    }
    
    // Cache GET requests (unless skipCache is set) - skip blob responses
    if (config.method === "get" && !config.skipCache && response.data && config?.responseType !== 'blob') {
      const cacheKey = cache.generateKey(config.url, config.params);
      const ttl = config.cacheTTL || 30 * 1000; // Default 30 seconds (reduced from 5 minutes)
      cache.set(cacheKey, response.data, ttl);
    }
    
    return response.data;
  },
  async (error) => {
    // Handle cached responses
    if (error.__cached) {
      return Promise.resolve(error.data);
    }
    
    const originalRequest = error.config;
    
    // Log error details for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      if (error.response) {
        console.error('[API Error]', {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.config?.url,
          method: error.config?.method,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        console.error('[API Error] No response received', {
          url: error.config?.url,
          method: error.config?.method
        });
      } else {
        console.error('[API Error] Request setup error', error.message);
      }
    }
    
    // Handle 429 Too Many Requests (Rate Limiting)
    if (error.response?.status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'] || error.response?.headers?.['Retry-After']
      const waitTime = retryAfter ? parseInt(retryAfter) : null
      const errorMessage = waitTime 
        ? `Too many requests. Please try again in ${waitTime} seconds.`
        : 'Too many requests. Please try again later.'
      
      // Show error toast if available
      if (typeof window !== 'undefined' && window.showError) {
        window.showError(errorMessage, waitTime ? (waitTime * 1000) : 5000)
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[API] Rate limit exceeded:', errorMessage)
      }
      
      return Promise.reject(error)
    }
    
    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if we're already on the refresh endpoint
      if (originalRequest.url?.includes('/auth/token/refresh')) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[API] Refresh token failed - logging out');
        }
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.detail ||
                           'Your session has expired. Please log in again.';
        if (globalShowError) {
          globalShowError(errorMessage, 6000);
        }
        clearTokens();
        clearAllCache();
        // Delay redirect to show error message
        setTimeout(() => {
          const currentPath = window.location.pathname;
          const loginPath = currentPath.startsWith('/admin') ? '/admin/login' : '/login';
          if (currentPath !== loginPath && !currentPath.endsWith('/login')) {
            window.location.href = loginPath;
          }
        }, 2000);
        return Promise.reject(error);
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[API] No refresh token available - logging out');
        }
        const errorMessage = 'Your session has expired. Please log in again.';
        if (globalShowError) {
          globalShowError(errorMessage, 6000);
        }
        clearTokens();
        clearAllCache();
        // Delay redirect to show error message
        setTimeout(() => {
          const currentPath = window.location.pathname;
          const loginPath = currentPath.startsWith('/admin') ? '/admin/login' : '/login';
          if (currentPath !== loginPath && !currentPath.endsWith('/login')) {
            window.location.href = loginPath;
          }
        }, 2000);
        return Promise.reject(error);
      }
      
      try {
        const response = await axios.post(
          `${baseURL}/api/v1/auth/token/refresh/`,
          { refresh: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
        
        // Backend returns: { success: true, data: { access: "...", refresh: "..." } }
        const responseData = response.data?.data || response.data;
        const access = responseData?.access || responseData?.access_token;
        const newRefresh = responseData?.refresh || responseData?.refresh_token;
        
        if (access) {
          saveAccessToken(access);
          
          // Handle refresh token rotation (backend rotates refresh tokens)
          if (newRefresh) {
            saveRefreshToken(newRefresh);
          }
          
          // Update the authorization header
          apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + access;
          originalRequest.headers['Authorization'] = 'Bearer ' + access;
          
          // Process queued requests
          processQueue(null, access);
          
          // Retry the original request
          return apiClient(originalRequest);
        } else {
          throw new Error('No access token in refresh response');
        }
      } catch (refreshError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[API] Token refresh failed:', refreshError);
        }
        processQueue(refreshError, null);
        
        const errorMessage = refreshError.response?.data?.message || 
                           refreshError.response?.data?.detail ||
                           'Your session has expired. Please log in again.';
        if (globalShowError) {
          globalShowError(errorMessage, 6000);
        }
        
        clearTokens();
        clearAllCache();
        
        // Delay redirect to show error message
        setTimeout(() => {
          const currentPath = window.location.pathname;
          const loginPath = currentPath.startsWith('/admin') ? '/admin/login' : '/login';
          if (currentPath !== loginPath && !currentPath.endsWith('/login')) {
            window.location.href = loginPath;
          }
        }, 2000);
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper to clear cache for specific endpoint
export const clearCache = (url, params = {}) => {
  const cacheKey = cache.generateKey(url, params);
  cache.delete(cacheKey);
};

// Helper to clear all cache
export const clearAllCache = () => {
  cache.clear();
};

export default apiClient;

